/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
const _ = require("lodash");

import { useState, useEffect } from "react";

// react-router-dom components
import { useNavigate } from "react-router-dom";

// @mui material components
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftAlert from "components/SoftAlert";

// Authentication layout components
import CoverLayout from "views/login/components";
import { authenticate, getData } from "Utils/server";
import { getAuthorizationHeader } from "Utils/helpers";

// Images
import newlogin0 from "assets/images/curved-images/curved9.jpg";
import newlogin1 from "assets/images/login/login_1.jpg";
import newlogin2 from "assets/images/login/login_2.jpg";
import newlogin3 from "assets/images/login/login_3.jpg";

// Soft UI Dashboard PRO React contexts
import { useSoftUIController, setPermissions } from "context";

const loginBgs = [newlogin0, newlogin1, newlogin2, newlogin3];
let bgSelected = loginBgs[Math.floor(Math.random() * (loginBgs.length - 1))];

function Login() {
  const navigate = useNavigate();

  const [controller, dispatch] = useSoftUIController();
  const { permissions } = controller;

  const [rememberMe, setRememberMe] = useState(false);
  const [apiCallResponseError, setApiCallResponseError] = useState(false);
  const [alertStatus, setAlertStatus] = useState(false);
  const [disableLogin, setDisableLogin] = useState(false);
  const [passwordType, setPasswordType] = useState(true);

  const customPlatformSorting = {
    56: 1,
    12: 2,
    5: 3,
    121: 4,
    119: 5,
    47: 6,
    17: 7,
    48: 8,
  };

  const routeChange = (path) => {
    navigate(path);
  };

  
  const handleSubmit = async (event) => {
    event.preventDefault();
    setDisableLogin(true);
    let { username, password } = document.forms[0];
    if (_.isEmpty(username.value) || _.isEmpty(password.value)) {
      setAlertStatus(true);
      setApiCallResponseError("Missing username or password");
      setDisableLogin(false);
      return;
    }

    let authEndpoint = "/api/users/auth";
    authEndpoint = "/api/v1/users/auth";
    let authResponse = await authenticate(authEndpoint, {
      username: username.value,
      password: password.value,
    });
    if (_.isEmpty(authResponse)) {
      setAlertStatus(true);
      setApiCallResponseError("Wrong username or password");
      setDisableLogin(false);
      return;
    }
    localStorage.setItem(`FTBCookie`, authResponse.data.token);
    //authResponse.data.user.defaultPlatform = authResponse.data.user.platforms[0];

    authResponse.data.user.platforms.map((p) => {
      p.odds_styles = p.odds_styles ? p.odds_styles.split(",") : [];
      p.odds_styles.unshift("decimal");
      if (customPlatformSorting[p.id]) p.sorting = customPlatformSorting[p.id];
      else p.sorting = p.id;
    });

    authResponse.data.user.platforms = authResponse.data.user.platforms.sort(
      (a, b) => a.sorting - b.sorting
    );

    localStorage.setItem(`userInfo`, JSON.stringify(authResponse.data.user));

    let userPermissions = [];
    let getPermissionsEndPoint = `/api/v1/users/${authResponse.data.user.id}/routepermissions`;
    let response = await getData(getPermissionsEndPoint, {
      Authorization: getAuthorizationHeader(),
    });

    if (response != null) {
      userPermissions = response.data ? response.data : [];
    }

    setPermissions(dispatch, userPermissions);

    let mainRoute = "/dashboard";
    switch (authResponse.data.user.role_id) {
      case 1:
        mainRoute = "/dashboard";
        break;
      case 2:
        mainRoute = "/dashboard";
        break;
      case 3:
        mainRoute = "/dashboard";
        break;
      case 4:
        mainRoute = "/dashboard";
        break;
      case 5:
        mainRoute = "/dashboard";
        break;

      default:
        break;
    }
    setAlertStatus(false);
    routeChange(`${mainRoute}`);
    setDisableLogin(false);
  };

  const handleSoftAlertStatuses = (status) => {
    //console.log("getting Status from Alert: ", status);
    setAlertStatus(false);
    setApiCallResponseError(false);
  };

  const handlePasswordType = () => {
    let passordtype = passwordType;
    setPasswordType(!passordtype);
  };

  const keyDownHandler = (event) => {
    if (event.code === "Enter") {
      handleSubmit(event);
    }
  };

  useEffect(() => {
    bgSelected = loginBgs[Math.floor(Math.random() * (loginBgs.length - 1))];
    const timeout = setTimeout(() => {
      // 👇️ redirects to an external URL
      localStorage.removeItem(`FTBCookie`);
      localStorage.removeItem(`userInfo`);
    }, 100);
  }, []);

  return (
    <CoverLayout
      title="Welcome back"
      description="Enter your username and password to sign in"
      image={bgSelected}
    >
      <SoftAlert
        className="topAlert"
        dismissible={true}
        status={alertStatus}
        callback={handleSoftAlertStatuses}
        fontSize="small"
        style={!apiCallResponseError ? { display: "none" } : { display: "flex" }}
        color={!apiCallResponseError ? "success" : "error"}
      >
        <Icon fontSize="small">notifications_active_outlined_icon </Icon>
        &nbsp;&nbsp;&nbsp;{apiCallResponseError}
      </SoftAlert>
      <SoftBox component="form" role="form">
        <SoftBox mb={2} lineHeight={1.25}>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Username
            </SoftTypography>
          </SoftBox>
          <SoftInput id="username" type="email" placeholder="Username" onKeyDown={keyDownHandler} />
        </SoftBox>
        <SoftBox mb={2} lineHeight={1.25}>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Password
            </SoftTypography>
          </SoftBox>
          <SoftInput
            id="password"
            type={passwordType ? "password" : "text"}
            placeholder="Password"
            icon={{
              component: passwordType ? "visibility_icon" : "visibility_off_icon",
              direction: "right",
            }}
            iconaction={handlePasswordType}
            iconclassname="password-eye-icon"
            onKeyDown={keyDownHandler}
          />
        </SoftBox>
        {/* <SoftBox display="flex" alignItems="center">
          <Switch checked={rememberMe} onChange={handleSetRememberMe} />
          <SoftTypography
            variant="button"
            fontWeight="regular"
            onClick={handleSetRememberMe}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            &nbsp;&nbsp;Remember me
          </SoftTypography>
        </SoftBox> */}
        <SoftBox mt={4} mb={1}>
          <SoftButton
            variant="gradient"
            color="info"
            fullWidth
            onClick={(e) => handleSubmit(e)}
            disabled={disableLogin}
            onKeyDown={keyDownHandler}
          >
            sign in
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </CoverLayout>
  );
}

export default Login;
